/**
 * MOB search AREA — the RNLI "Rapid Response Method" (old ALB card TP-ALB-133).
 *
 * Where the drift estimate (mobDrift) gives us a single point — the datum, and
 * the straight-line resultant drift from the drift start position to it — this
 * module turns that into a patch of sea to actually search:
 *   1. a SEARCH RADIUS built from the drift and the fix error,
 *   2. a square SEARCH BOX around the datum that encloses the search circle,
 *   3. a ground-based AREA search (parallel track or creeping line) that covers
 *      the box lane by lane.
 *
 * Two important differences from the sector / expanding-square patterns:
 *   - Those are THROUGH-THE-WATER patterns flown on a heading and a stopwatch, so
 *     we deliberately do NOT plot them. The area searches here are GROUND-based
 *     (run over the ground, on COG), so they CAN and SHOULD be plotted on the
 *     chart as leg lines.
 *   - The rapid-response method is only valid for a unit arriving within about
 *     an hour of the drift start. Past that a full search area including
 *     DIVERGENCE is needed, which the supplied cards do not cover, so we flag it
 *     honestly rather than inventing a divergence calculation.
 */
import { rhumbDestination } from '@/nav';
import { cardinalFor } from './searchPatterns';

// -----------------------------------------------------------------------------
// IPE — Initial Position Error (Navigational Fix Error)
// -----------------------------------------------------------------------------

/**
 * A pickable Initial Position Error value, in nautical miles, from the card's
 * table. `nm` is the value we add to the drift error; where the card gives a
 * range (SATNAV / DGPS is 0.1–0.5), `nm` is the sensible mid default and `note`
 * carries the range so the UI can show it.
 */
export interface IpeOption {
  id: string;
  label: string;
  nm: number;
  note?: string;
}

/**
 * The card's IPE table. The "unknown craft" rows are the values the SMC (Search
 * Mission Co-ordinator) assigns when the casualty's own fix error isn't known,
 * scaled by how big and how findable the craft is.
 */
export const IPE_OPTIONS: readonly IpeOption[] = [
  { id: 'ins', label: 'Inertial Navigation System', nm: 0 },
  { id: 'satnav', label: 'SATNAV / DGPS', nm: 0.3, note: '0.1–0.5 nm' },
  { id: 'visual3', label: 'Visual three-line fix', nm: 1.0 },
  { id: 'radar', label: 'Radar fix', nm: 1.0 },
  { id: 'celestial3', label: 'Celestial three-line fix', nm: 2.0 },
  { id: 'beacon3', label: 'Marine radio beacon (3-beacon)', nm: 4.0 },
  {
    id: 'unknown_large',
    label: 'Unknown craft — ship / military submarine / aircraft >2 engines',
    nm: 5,
    note: 'SMC assigns',
  },
  {
    id: 'unknown_twin',
    label: 'Unknown craft — twin-engine aircraft',
    nm: 10,
    note: 'SMC assigns',
  },
  {
    id: 'unknown_small',
    label: 'Unknown craft — boat / submersible / single-engine aircraft',
    nm: 15,
    note: 'SMC assigns',
  },
];

/** Default IPE: SATNAV / DGPS at 0.3 nm, the common small-craft fix. */
export const DEFAULT_IPE_ID = 'satnav';
export const DEFAULT_IPE_NM = 0.3;

/** Look an IPE option up by id, or undefined if it isn't a table row (manual). */
export function ipeOptionById(id: string): IpeOption | undefined {
  return IPE_OPTIONS.find((o) => o.id === id);
}

// -----------------------------------------------------------------------------
// Search radius (drift error + IPE)
// -----------------------------------------------------------------------------

export interface SearchRadiusResult {
  /** One third of the resultant drift distance, nm. */
  driftErrorNm: number;
  /** The Initial Position Error we used, nm. */
  ipeNm: number;
  /** SEARCH RADIUS = drift error + IPE, nm. */
  radiusNm: number;
}

/**
 * SEARCH RADIUS = (resultant drift distance ÷ 3) + IPE.
 *
 * The "drift error" is one third of the straight-line resultant drift from the
 * drift start position to the datum — the further the casualty has drifted, the
 * less sure we are exactly where they ended up, so the circle grows with it. On
 * top of that we add the Initial Position Error (how well we knew the start
 * position in the first place). Both are clamped non-negative so a nonsense
 * negative input can't shrink the circle.
 */
export function searchRadiusNm(resultantDriftNm: number, ipeNm: number): SearchRadiusResult {
  const driftErrorNm = Math.max(0, resultantDriftNm) / 3;
  const ipe = Math.max(0, ipeNm);
  return { driftErrorNm, ipeNm: ipe, radiusNm: driftErrorNm + ipe };
}

// -----------------------------------------------------------------------------
// Search box (square around the datum, enclosing the search circle)
// -----------------------------------------------------------------------------

/**
 * Optional overrides for the box. Left undefined, the box is the datum-centred
 * square that just encloses the search circle (side = 2 × radius, orientation
 * north-up). The skipper can widen, lengthen, or turn it later.
 */
export interface SearchBoxOptions {
  /** Box dimension along the orientation axis (N–S when orientation 0), nm. */
  lengthNm?: number;
  /** Box dimension across the orientation axis (E–W when orientation 0), nm. */
  widthNm?: number;
  /** Bearing of the box's length axis, °T. 0 = length runs N–S. */
  orientationDeg?: number;
}

/**
 * A square/rectangular search box with its four RNLI-lettered corners. With the
 * default north-up orientation A is the NW corner, B the NE, C the SE, D the SW,
 * so ABCD runs clockwise from the top-left.
 */
export interface SearchBox {
  centre: [number, number];
  /** Top-left / NW corner. */
  A: [number, number];
  /** Top-right / NE corner. */
  B: [number, number];
  /** Bottom-right / SE corner. */
  C: [number, number];
  /** Bottom-left / SW corner. */
  D: [number, number];
  /** Closed ring A→B→C→D→A, for drawing. */
  ring: [number, number][];
  lengthNm: number;
  widthNm: number;
  orientationDeg: number;
}

/** Two rhumb hops from the centre: `along` on the length axis, `across` on the width axis. */
function corner(
  centre: [number, number],
  orientationDeg: number,
  along: number,
  across: number,
): [number, number] {
  const up = ((orientationDeg % 360) + 360) % 360;
  const right = (up + 90) % 360;
  const p = rhumbDestination(centre, up, along);
  return rhumbDestination(p, right, across);
}

/**
 * Build the search box around the datum. By default a square of side 2 × radius
 * (so it encloses the whole search circle) centred on the datum and oriented
 * north-up; length, width, and orientation can each be overridden.
 *
 * Half a length up and half a width across from the centre gives each corner:
 * A is up-and-left (NW), B up-and-right (NE), C down-and-right (SE), D
 * down-and-left (SW), the RNLI lettering.
 */
export function searchBoxFromRadius(
  datum: [number, number],
  radiusNm: number,
  opts?: SearchBoxOptions,
): SearchBox {
  const side = Math.max(0, radiusNm) * 2;
  const lengthNm = opts?.lengthNm != null && opts.lengthNm > 0 ? opts.lengthNm : side;
  const widthNm = opts?.widthNm != null && opts.widthNm > 0 ? opts.widthNm : side;
  const orientationDeg = ((opts?.orientationDeg ?? 0) % 360 + 360) % 360;
  const hl = lengthNm / 2;
  const hw = widthNm / 2;
  const A = corner(datum, orientationDeg, hl, -hw);
  const B = corner(datum, orientationDeg, hl, hw);
  const C = corner(datum, orientationDeg, -hl, hw);
  const D = corner(datum, orientationDeg, -hl, -hw);
  return {
    centre: datum,
    A,
    B,
    C,
    D,
    ring: [A, B, C, D, A],
    lengthNm,
    widthNm,
    orientationDeg,
  };
}

// -----------------------------------------------------------------------------
// Area search: parallel track / creeping line
// -----------------------------------------------------------------------------

export type AreaPatternId = 'parallel_track' | 'creeping_line';

export function isAreaPattern(id: string): id is AreaPatternId {
  return id === 'parallel_track' || id === 'creeping_line';
}

export interface AreaLeg {
  /** 1-based leg number, in the order flown. */
  legNumber: number;
  /**
   * A long search leg down a lane, or the short cross-over step between lanes.
   * The cross legs are the turns; the count that matters for coverage is the
   * search legs (one per lane).
   */
  kind: 'search' | 'cross';
  /** Course over the ground to steer this leg, °T (this is a ground-based search). */
  cogDeg: number;
  /** Nearest 16-point cardinal for the COG. */
  cardinal: string;
  /** Leg length over the ground, nm. */
  lengthNm: number;
  /** Search speed over the ground, kn. */
  speedKn: number;
  /** Time to run this leg at the search speed, seconds. */
  timeSec: number;
}

export interface AreaSearch {
  id: AreaPatternId;
  /** Legs in the order flown (search + cross), for the compact summary. */
  legs: AreaLeg[];
  /** Number of long search legs = number of lanes across the box. */
  searchLegCount: number;
  /** The plotted polyline within the box, [lon, lat] vertices. */
  vertices: [number, number][];
  /** Commence Search Position — the first vertex, half a track space in. */
  csp: [number, number];
  trackSpacingNm: number;
  /** Length of each long search leg, nm. */
  longSpanNm: number;
  /** Distance covered across the box by the lanes, nm. */
  crossSpanNm: number;
  /** Direction the long legs run, °T. */
  legDirDeg: number;
  /** Direction the search advances lane to lane, °T. */
  advanceDirDeg: number;
  totalDistanceNm: number;
  totalTimeSec: number;
  notes: string[];
}

function legSeconds(lengthNm: number, speedKn: number): number {
  if (speedKn <= 0) return Infinity;
  return (lengthNm / speedKn) * 3600;
}

/**
 * Generate a ground-based area search over the box.
 *
 * Parallel track runs the long legs parallel to the LONGEST edge of the box;
 * creeping line runs them parallel to the SHORTEST edge (so the legs are shorter
 * and there are more of them, creeping along the long axis). Everything else is
 * the same: lanes one track spacing apart, the first lane half a track space in
 * from the edge so we don't miss the strip along the boundary, and the legs run
 * boustrophedon — up one lane, one track space across, back down the next.
 *
 * The number of lanes is ceil(cross span ÷ track spacing) so the whole box is
 * covered. Ground-based, so the headings are COG over the ground and the times
 * use speed over the ground.
 */
export function areaSearchLegs(
  id: AreaPatternId,
  box: SearchBox,
  trackSpacingNm: number,
  speedKn: number,
): AreaSearch {
  const spacing = Math.max(1e-6, trackSpacingNm);
  // The box's two axes: A along the orientation (length), B across it (width).
  const axisA = { dir: box.orientationDeg, extent: box.lengthNm };
  const axisB = { dir: (box.orientationDeg + 90) % 360, extent: box.widthNm };
  const longAxis = axisA.extent >= axisB.extent ? axisA : axisB;
  const shortAxis = axisA.extent >= axisB.extent ? axisB : axisA;

  // Parallel track: legs along the longest edge, advance across the short span.
  // Creeping line: legs along the shortest edge, advance across the long span.
  const legAxis = id === 'parallel_track' ? longAxis : shortAxis;
  const advAxis = id === 'parallel_track' ? shortAxis : longAxis;
  const legDirDeg = legAxis.dir;
  const advanceDirDeg = advAxis.dir;
  const longSpanNm = legAxis.extent;
  const crossSpanNm = advAxis.extent;

  const lanes = Math.max(1, Math.ceil(crossSpanNm / spacing));
  const halfLong = longSpanNm / 2;

  const move = (from: [number, number], bearing: number, dist: number): [number, number] =>
    rhumbDestination(from, bearing, dist);

  const vertices: [number, number][] = [];
  const legs: AreaLeg[] = [];
  let legNumber = 0;
  let forward = true;
  let prevEnd: [number, number] | null = null;

  for (let j = 0; j < lanes; j++) {
    // Lane centre offset from the datum along the advance axis: start half a
    // track in from the near edge, then one track space per lane.
    const offset = -crossSpanNm / 2 + (j + 0.5) * spacing;
    const laneCentre = move(box.centre, advanceDirDeg, offset);
    const fwdEnd = move(laneCentre, legDirDeg, halfLong);
    const bwdEnd = move(laneCentre, legDirDeg, -halfLong);
    const start = forward ? bwdEnd : fwdEnd;
    const end = forward ? fwdEnd : bwdEnd;

    if (prevEnd) {
      // Cross-over step from the previous lane's end to this lane's start.
      vertices.push(start);
      legNumber += 1;
      legs.push({
        legNumber,
        kind: 'cross',
        cogDeg: advanceDirDeg,
        cardinal: cardinalFor(advanceDirDeg),
        lengthNm: spacing,
        speedKn,
        timeSec: legSeconds(spacing, speedKn),
      });
    } else {
      vertices.push(start);
    }

    vertices.push(end);
    legNumber += 1;
    const cog = forward ? legDirDeg : (legDirDeg + 180) % 360;
    legs.push({
      legNumber,
      kind: 'search',
      cogDeg: cog,
      cardinal: cardinalFor(cog),
      lengthNm: longSpanNm,
      speedKn,
      timeSec: legSeconds(longSpanNm, speedKn),
    });

    prevEnd = end;
    forward = !forward;
  }

  const totalDistanceNm = legs.reduce((s, l) => s + l.lengthNm, 0);
  const totalTimeSec = legs.reduce((s, l) => s + l.timeSec, 0);

  return {
    id,
    legs,
    searchLegCount: lanes,
    vertices,
    csp: vertices[0],
    trackSpacingNm: spacing,
    longSpanNm,
    crossSpanNm,
    legDirDeg,
    advanceDirDeg,
    totalDistanceNm,
    totalTimeSec,
    notes: [
      id === 'parallel_track'
        ? 'Parallel track — long legs run parallel to the longest edge of the box.'
        : 'Creeping line — long legs run parallel to the shortest edge of the box.',
      'Ground-based search: steer the COG shown and use speed over the ground, so it plots on the chart.',
      'Start at the CSP, half a track space in from the corner, and turn at each box edge.',
    ],
  };
}
